import React, { ChangeEvent, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useInfiniteQuery } from '@tanstack/react-query';
import { FlightTextInput } from '@flybits/design-system';
import useDebounce from 'hooks/useDebounce';
import { Content } from 'interface/content/content.interface';
import useSettings from 'hooks/useSetting';
import { getDefaultLanguage } from 'helpers/templated-experience.helper';
import { getStepMetadata } from 'store/journey/journey.selector';
import { PaginationResponse } from 'interface/shared/api.interface';
import ContentAPI from 'services/api/content.api';
import { fetchContentInstance } from 'store/content/content.thunk';
import { useAppSelector as useSelector } from 'hooks/reduxHooks';
import { getContentListItems } from 'store/content/content.selector';
import { ContentListItem } from '../types';
import './ContentInstancesSelector.scss';

type TContentSelectorProps = {
  selectedContentInstances: Content[];
  setSelectedContentInstances: React.Dispatch<React.SetStateAction<Content[]>>;
  stepIdx: number;
  isSingleSelectMode?: boolean;
  singleSelectContentId?: string;
  singleSelectTemplateIdConstraint?: string;
  setSingleSelectContentId?: React.Dispatch<React.SetStateAction<string>>;
  selectedExternalContent?: { refId: string; id: string };
  setSelectedExternalContent: React.Dispatch<React.SetStateAction<{ refId: string; id: string } | undefined>>;
  stepHasPush: boolean;
  contentManagerSingleSelectOnly?: boolean;
};

type TStepContentMetadata = {
  contentId: string;
  stepIdx: number;
  stepName: string;
  refId: string;
};

enum Tabs {
  'ALL',
  'SELECTED',
}

const MAIN_CLASS = 'content-selector';
const CLASSES = {
  SEARCH_WRAPPER: `${MAIN_CLASS}__search-wrapper`,
  TAB_LIST: `${MAIN_CLASS}__tab-list`,
  TAB: `${MAIN_CLASS}__tab`,
  CONTENT_LIST: `${MAIN_CLASS}__content-list`,
  CONTENT_ITEM: `${MAIN_CLASS}__content-item`,
  CONTENT_SKELETON: `${MAIN_CLASS}__content-skeleton`,
  CONTENT_ICON: `${MAIN_CLASS}__content-icon`,
  CONTENT_NAME: `${MAIN_CLASS}__content-name`,
  CONTENT_DESCRIPTION: `${MAIN_CLASS}__content-description`,
  LOADER_WRAPPER: `${MAIN_CLASS}__loader-wrapper`,
  NO_INSTANCES: `${MAIN_CLASS}__no-items`,
};

const ContentInstancesSelector: React.FC<TContentSelectorProps> = ({
  selectedContentInstances,
  setSelectedContentInstances,
  stepIdx,
  isSingleSelectMode = false,
  singleSelectTemplateIdConstraint = '',
  setSingleSelectContentId = () => null,
  singleSelectContentId,
  selectedExternalContent,
  setSelectedExternalContent,
  contentManagerSingleSelectOnly = false,
}) => {
  const contentAPI = useMemo(() => new ContentAPI(), []);

  const [contentInstancesCurrentlyLoading, setContentInstancesCurrentlyLoading] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState<Tabs>(Tabs.ALL);
  const [lastElement, setLastElement] = useState<HTMLElement | null>(null);
  const { languages } = useSettings();
  const defaultLang = getDefaultLanguage(languages);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const fetchContentInstances = async ({ pageParam = 0 }) =>
    await contentAPI.getContentInstances({
      limit: 30,
      offset: pageParam,
      labelsFormula: '(!fb-auto-gen)', // we don't want to show auto-generated content instances
      search: debouncedSearchTerm,
      managementMode: true,
    });

  const {
    isLoading,
    isFetchingNextPage,
    data: contentInstances,
    fetchNextPage,
  } = useInfiniteQuery<PaginationResponse<Content>>({
    queryKey: ['content-instances', debouncedSearchTerm],
    queryFn: fetchContentInstances,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.offset + lastPage.pagination.limit > lastPage.pagination.totalRecords
        ? undefined
        : lastPage.pagination.offset + lastPage.pagination.limit,
  });

  const observer = useRef(
    new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      {
        threshold: 0.25,
      },
    ),
  );

  const getConstrainedInstances = useCallback(() => {
    const hasConstraint = isSingleSelectMode && singleSelectTemplateIdConstraint;
    const constrainedInstances =
      (hasConstraint
        ? contentInstances?.pages
            .flatMap((page) => page.data)
            .filter((ci) => ci.templateId === singleSelectTemplateIdConstraint)
        : contentInstances?.pages.flatMap((page) => page.data)) || [];
    return selectedTab === Tabs.ALL ? constrainedInstances : selectedContentInstances;
  }, [isSingleSelectMode, singleSelectTemplateIdConstraint, contentInstances, selectedTab, selectedContentInstances]);

  const filteredContentInstances = getConstrainedInstances();

  const handleTabChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.target;
    setSelectedTab(+value);
  };

  const [lastCheckedContentId, setLastCheckedContentId] = useState(
    isSingleSelectMode && singleSelectContentId ? singleSelectContentId : '',
  );

  const handleContentInstancesChange = async (evt: ChangeEvent<HTMLInputElement>, externalRefId?: string) => {
    const { value, checked } = evt.target;

    if (isSingleSelectMode || contentManagerSingleSelectOnly) {
      if (checked) {
        setSingleSelectContentId(value);
        externalRefId && setSelectedExternalContent({ refId: externalRefId, id: value });
      } else {
        setSingleSelectContentId('');
        externalRefId && setSelectedExternalContent(undefined);
      }
    }

    if (checked) {
      setLastCheckedContentId(value);
      setSelectedContentInstances((prevState) => [
        ...prevState,
        {
          id: value,
        },
      ]);
      externalRefId && setSelectedExternalContent({ refId: externalRefId, id: value });
      setContentInstancesCurrentlyLoading((prevState) => [...prevState, value]);
      const content = await fetchContentInstance(value, { getJourneyUsages: true });
      setContentInstancesCurrentlyLoading((prevState) => prevState.filter((c) => c !== value));
      if (!content) return;
      setSelectedContentInstances((prevState) => {
        const newState = [...prevState];
        newState.splice(
          prevState.findIndex((c) => c.id === value),
          1,
          content,
        );
        return newState;
      });
    } else {
      setLastCheckedContentId('');
      setSelectedContentInstances((prevState) => {
        const tempSelectedContents = [...prevState];
        tempSelectedContents.splice(
          tempSelectedContents.findIndex((t) => t.id === value),
          1,
        );
        return tempSelectedContents;
      });
      externalRefId && setSelectedExternalContent(undefined);
    }
  };

  const contentsFromState: ContentListItem[] = useSelector((state) => getContentListItems(state, stepIdx));
  const contentIdsInUse = useSelector((state) => {
    const contents: { [key: string]: TStepContentMetadata } = {};
    const journeySteps = state.te.journey.steps;

    journeySteps.forEach((step, idx) => {
      step.content.forEach((content) => {
        const contentId = state.te.content.byRefId[content]?.id;
        const contentRefId = state.te.content.byRefId[content]?.refId;

        if (!contentId || !contentRefId) return;

        contents[contentId] = {
          contentId,
          stepIdx: idx,
          refId: contentRefId,
          stepName: getStepMetadata(state.te.journey, idx).name,
        };
      });
    });

    return contents;
  });

  const renderContentInstancesList = () => {
    if (!isLoading && filteredContentInstances.length === 0) {
      return (
        <li className={CLASSES.NO_INSTANCES}>
          <p>No items to show</p>
        </li>
      );
    }

    return filteredContentInstances.map((content, i) => {
      if (selectedTab === Tabs.ALL && contentsFromState?.findIndex((c) => c.id === content.id) > -1) {
        return null;
      }

      const contentIdInUseMetadata = content.id ? contentIdsInUse[content.id] : undefined;
      const isExternalContent = !!contentIdInUseMetadata && contentIdInUseMetadata.stepIdx !== stepIdx;
      const disabled =
        (selectedTab === Tabs.SELECTED && contentsFromState?.findIndex((c) => c.id === content.id) > -1) ||
        ((isSingleSelectMode || contentManagerSingleSelectOnly) &&
          !!lastCheckedContentId &&
          lastCheckedContentId !== content.id) ||
        (isExternalContent && !!selectedExternalContent?.id && selectedExternalContent.id !== content.id) ||
        contentInstancesCurrentlyLoading.includes(content.id || '');
      const contentName = `${content.localizations?.[defaultLang]?.name}${
        isExternalContent ? ` (From ${contentIdInUseMetadata.stepName})` : ''
      }`;

      return (
        <li
          className={CLASSES.CONTENT_ITEM}
          key={content.id}
          ref={i === filteredContentInstances.length - 1 ? setLastElement : null}
        >
          <input
            type="checkbox"
            value={content.id}
            name="content"
            id={`content-${content.id}`}
            onChange={(e) =>
              handleContentInstancesChange(e, isExternalContent ? contentIdInUseMetadata.refId : undefined)
            }
            checked={selectedContentInstances?.findIndex((c) => c.id === content.id) > -1}
            disabled={disabled}
          />
          <label htmlFor={`content-${content.id}`}>
            <div className={CLASSES.CONTENT_NAME}>{contentName}</div>
          </label>
        </li>
      );
    });
  };

  useEffect(() => {
    const currentObserver = observer.current;
    if (lastElement && selectedTab === Tabs.ALL) {
      currentObserver.observe(lastElement);
    }
    return () => {
      if (lastElement) {
        currentObserver.unobserve(lastElement);
      }
    };
  }, [observer, lastElement, selectedTab]);

  const SkeletonTemplate = () => (
    <li className={`${CLASSES.CONTENT_ITEM} ${CLASSES.CONTENT_SKELETON}`}>
      <div>
        <Skeleton width="14px" height="14px" />
      </div>
      <div>
        <Skeleton width="48%" />
      </div>
    </li>
  );

  return (
    <div className={MAIN_CLASS}>
      <div className={CLASSES.SEARCH_WRAPPER}>
        <FlightTextInput
          trailingIcon="search"
          width="100%"
          value={searchTerm}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onChange={(evt: any) => setSearchTerm(evt.target.value)}
          placeholderText="Search here for Contents"
        />
      </div>
      {!contentManagerSingleSelectOnly && (
        <div className={CLASSES.TAB_LIST}>
          <div className={CLASSES.TAB}>
            <input
              type="radio"
              name="tab"
              id="tab-all"
              value={Tabs.ALL}
              checked={selectedTab === Tabs.ALL}
              onChange={handleTabChange}
            />
            <label htmlFor="tab-all">All</label>
          </div>
          <div className={CLASSES.TAB}>
            <input
              type="radio"
              name="tab"
              id="tab-selected"
              value={Tabs.SELECTED}
              checked={selectedTab === Tabs.SELECTED}
              onChange={handleTabChange}
            />
            <label htmlFor="tab-selected">Selected</label>
          </div>
        </div>
      )}
      <ul className={CLASSES.CONTENT_LIST}>
        {isLoading && [...Array(3)].map((item, index) => <SkeletonTemplate key={index} />)}
        {renderContentInstancesList()}
        {isFetchingNextPage && <SkeletonTemplate />}
      </ul>
    </div>
  );
};

export default ContentInstancesSelector;
